import { useI18n } from '@ecom/i18n';
import { Badge, EmptyState } from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import { useCustomersQuery } from '../api';
import { CustomerDetailDrawer } from '../components/CustomerDetailDrawer';
import { CustomerTable } from '../components/CustomerTable';
import type { CustomerSummary } from '../types';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';

/**
 * "Clients" — aucun endpoint marchand dédié n'existe pour lister les
 * clients (`customers`/`customer_store_profiles`, DEV §6.4, n'ont pas de
 * route côté `app_user`) ; cet écran agrège les vraies commandes déjà
 * exposées par `GET /merchant/orders` plutôt que d'inventer un nouveau
 * point d'entrée backend — voir `features/orders/api.ts`'s
 * `useAllOrdersQuery` et `features/clients/api.ts`'s propre en-tête.
 */
export function CustomerListPage(): ReactElement {
  const { t } = useI18n();
  const { customers, truncated, isPending, isError } = useCustomersQuery();
  const [selected, setSelected] = useState<CustomerSummary | null>(null);

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div>
        <h1 className="sk-page-title">{t('merchant.nav.customers')}</h1>
        <p className="text-sm text-inkFaint">{t('merchant.clients.subtitle')}</p>
      </div>

      {truncated ? (
        <div className="flex items-start gap-2 rounded-md border border-line bg-surface px-3 py-2.5">
          <Badge tone="info">{t('merchant.clients.truncatedNotice')}</Badge>
          <p className="text-sm text-inkFaint">{t('merchant.clients.truncatedDescription')}</p>
        </div>
      ) : null}

      {isPending ? (
        <TableSkeleton columns={6} />
      ) : isError ? (
        <EmptyState title={t('merchant.clients.loadError')} />
      ) : customers.length === 0 ? (
        <EmptyState
          title={t('merchant.clients.emptyTitle')}
          description={t('merchant.clients.emptyDescription')}
        />
      ) : (
        <CustomerTable customers={customers} onSelect={setSelected} />
      )}

      {selected !== null ? (
        <CustomerDetailDrawer
          customer={selected}
          onClose={() => {
            setSelected(null);
          }}
        />
      ) : null}
    </div>
  );
}
